import { useId, useRef, useState } from 'react';

import { Button, Input, Popover, Text, XStack, YStack } from '@/components/ui';

import type { TranscriptTermCorrectionController } from './meetings-transcript-correction';

export function TranscriptTermCorrectionPopover({
  controller,
}: {
  controller: TranscriptTermCorrectionController;
}) {
  const { selection, isSaving, cancel, save } = controller;
  const titleId = useId();
  const focusTarget = useRef<HTMLElement | undefined>(undefined);
  if (selection) focusTarget.current = selection.focusTarget;

  return (
    <Popover.Root
      open={!!selection}
      onOpenChange={(open) => {
        if (!open && !isSaving) cancel();
      }}
    >
      <Popover.Anchor asChild>
        <span
          aria-hidden
          className="fixed pointer-events-none"
          style={
            selection
              ? {
                  left: selection.rect.left,
                  top: selection.rect.top,
                  width: selection.rect.width,
                  height: selection.rect.height,
                }
              : undefined
          }
        />
      </Popover.Anchor>
      {selection && (
        <Popover.Content
          aria-labelledby={titleId}
          aria-busy={isSaving}
          align="center"
          side="top"
          sideOffset={0}
          className="w-80 p-4"
          data-find-exclude
          data-no-copy
          onEscapeKeyDown={(event) => {
            if (isSaving) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (isSaving) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (focusTarget.current?.isConnected) {
              focusTarget.current.focus({ preventScroll: true });
            }
          }}
        >
          <TranscriptTermCorrectionForm
            key={selection.term}
            titleId={titleId}
            heardTerm={selection.term}
            isSaving={isSaving}
            onCancel={cancel}
            onSave={save}
          />
          <Popover.Arrow
            aria-hidden
            width={14}
            height={7}
            className="fill-surface drop-shadow-[0_1px_0_var(--border-strong)]"
          />
        </Popover.Content>
      )}
    </Popover.Root>
  );
}

function TranscriptTermCorrectionForm({
  titleId,
  heardTerm,
  isSaving,
  onCancel,
  onSave,
}: {
  titleId: string;
  heardTerm: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (correctedTerm: string) => void;
}) {
  const [correctedTerm, setCorrectedTerm] = useState('');
  const replacement = correctedTerm.trim();
  const canSave =
    !!replacement &&
    replacement !== heardTerm &&
    Array.from(replacement).length <= 200 &&
    !/[\r\n\0]/u.test(replacement) &&
    !isSaving;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) onSave(replacement);
      }}
    >
      <YStack className="gap-3">
        <Text id={titleId} fontWeight="medium" size="sm">
          Correct transcript
        </Text>
        <Text size="sm" variant="muted">
          Replace every occurrence of “{heardTerm}” in this transcript and add
          the corrected term to your vocabulary.
        </Text>
        <Input
          autoFocus
          inputSize="sm"
          aria-label="Replace with"
          placeholder="Replace with…"
          value={correctedTerm}
          disabled={isSaving}
          onChange={(event) => setCorrectedTerm(event.target.value)}
        />
        <XStack className="justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isSaving}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={isSaving}
            disabled={!canSave}
          >
            Save
          </Button>
        </XStack>
      </YStack>
    </form>
  );
}
